import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import { 
  Building2, 
  Calendar, 
  Clock, 
  ArrowLeft,
  Leaf,
  CheckCircle,
  AlertTriangle,
  FileText,
  Target,
  Scale
} from 'lucide-react';

export default function CritereEnvironnementalArticle() {
  return (
    <>
      <SEO 
        title="Critère Environnemental Obligatoire Marchés Publics 2026"
        description="À partir de 2026, le critère environnemental devient obligatoire dans les marchés publics. Guide complet pour les entreprises."
        path="/blog/critere-environnemental-marches-publics-2026"
      />
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                Réglementation
              </span>
              <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                NOUVEAU
              </span>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Critère Environnemental Obligatoire : Ce Qui Change au 22 Août 2026
            </h1>
            <div className="flex items-center gap-6 text-slate-500">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                16 février 2026
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                10 min de lecture
              </span>
            </div>
          </div>

          {/* Featured Image Placeholder */}
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-12 mb-12 text-white text-center">
            <Leaf className="w-20 h-20 mx-auto mb-4 opacity-90" />
            <p className="text-xl font-medium">Loi Climat & Résilience - Article 35</p>
            <p className="text-green-100">Entrée en vigueur : 22 août 2026</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              À partir du <strong>22 août 2026</strong>, une révolution silencieuse va transformer la commande publique française. 
              L'article 35 de la loi Climat & Résilience impose désormais l'intégration d'un <strong>critère environnemental 
              dans TOUS les marchés publics</strong>. Fini l'attribution sur le seul critère du prix. Décryptage complet.
            </p>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-green-900 mb-2">Impact Critique - 10/10</h4>
                  <p className="text-green-800">
                    Cette obligation concerne TOUS les marchés publics, quelle que soit leur valeur ou leur nature. 
                    Les acheteurs ne pourront plus attribuer un marché sur le seul critère du prix le plus bas.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Scale className="w-7 h-7 text-green-600" />
              Ce que dit la loi
            </h2>

            <p>
              La loi Climat & Résilience du 22 août 2021 prévoyait une entrée en vigueur différée de 5 ans pour 
              l'obligation d'intégrer un critère environnemental. Cette échéance arrive le <strong>22 août 2026</strong>.
            </p>

            <div className="bg-slate-100 rounded-xl p-6 my-8">
              <h4 className="font-bold text-slate-900 mb-4">Article L2152-7-1 du Code de la Commande Publique (nouvelle rédaction)</h4>
              <p className="text-slate-700 italic">
                "Les critères d'attribution du marché comprennent un critère prenant en compte les caractéristiques 
                environnementales de l'offre. Ce critère peut notamment prendre en compte l'impact environnemental 
                sur l'ensemble du cycle de vie des produits ou prestations objets du marché."
              </p>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Target className="w-7 h-7 text-green-600" />
              Quels marchés sont concernés ?
            </h2>

            <p><strong>Tous les marchés publics</strong>, sans exception :</p>

            <ul className="space-y-3 my-6">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                <span><strong>Marchés de travaux</strong> : construction, rénovation, voirie, génie civil</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                <span><strong>Marchés de fournitures</strong> : équipements, matériels, consommables</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                <span><strong>Marchés de services</strong> : prestations intellectuelles, maintenance, formation</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                <span><strong>Procédures adaptées (MAPA)</strong> : y compris les petits marchés</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                <span><strong>Procédures formalisées</strong> : appels d'offres, procédures négociées</span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <FileText className="w-7 h-7 text-green-600" />
              Comment intégrer le critère environnemental ?
            </h2>

            <p>
              Les acheteurs publics disposent de plusieurs leviers pour intégrer ce critère dans leurs consultations :
            </p>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h4 className="font-bold text-slate-900 mb-3">1. Critères d'attribution</h4>
                <ul className="text-slate-600 space-y-2 text-sm">
                  <li>• Performance environnementale du produit</li>
                  <li>• Analyse du cycle de vie (ACV)</li>
                  <li>• Empreinte carbone</li>
                  <li>• Labels et certifications écologiques</li>
                </ul>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h4 className="font-bold text-slate-900 mb-3">2. Conditions d'exécution</h4>
                <ul className="text-slate-600 space-y-2 text-sm">
                  <li>• Utilisation de matériaux recyclés</li>
                  <li>• Gestion des déchets de chantier</li>
                  <li>• Véhicules propres pour les livraisons</li>
                  <li>• Réduction des émissions</li>
                </ul>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h4 className="font-bold text-slate-900 mb-3">3. Spécifications techniques</h4>
                <ul className="text-slate-600 space-y-2 text-sm">
                  <li>• Exigences de performance énergétique</li>
                  <li>• Standards environnementaux minimaux</li>
                  <li>• Références à des normes ISO 14000</li>
                  <li>• Écolabels européens</li>
                </ul>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h4 className="font-bold text-slate-900 mb-3">4. Variantes environnementales</h4>
                <ul className="text-slate-600 space-y-2 text-sm">
                  <li>• Autorisation de variantes "vertes"</li>
                  <li>• Solutions alternatives innovantes</li>
                  <li>• Économie circulaire</li>
                  <li>• Réemploi de matériaux</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Pondération recommandée du critère environnemental
            </h2>

            <p>
              Bien que la loi n'impose pas de pondération minimale, les bonnes pratiques recommandent :
            </p>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 my-8">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-green-200">
                    <th className="text-left py-3 text-green-900">Type de marché</th>
                    <th className="text-left py-3 text-green-900">Pondération conseillée</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <tr className="border-b border-green-100">
                    <td className="py-3">Fournitures courantes</td>
                    <td className="py-3 font-medium">10% à 15%</td>
                  </tr>
                  <tr className="border-b border-green-100">
                    <td className="py-3">Services intellectuels</td>
                    <td className="py-3 font-medium">5% à 10%</td>
                  </tr>
                  <tr className="border-b border-green-100">
                    <td className="py-3">Travaux de construction</td>
                    <td className="py-3 font-medium">15% à 25%</td>
                  </tr>
                  <tr>
                    <td className="py-3">Rénovation énergétique</td>
                    <td className="py-3 font-medium">20% à 30%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Comment préparer votre entreprise ?
            </h2>

            <div className="space-y-4 my-8">
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h4 className="font-bold text-slate-900">Réalisez votre bilan carbone</h4>
                  <p className="text-slate-600">Mesurez l'empreinte environnementale de vos activités pour pouvoir la valoriser dans vos réponses.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h4 className="font-bold text-slate-900">Obtenez des certifications</h4>
                  <p className="text-slate-600">ISO 14001, écolabels, labels RSE : ces certifications deviendront des arguments différenciants majeurs.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h4 className="font-bold text-slate-900">Formez vos équipes</h4>
                  <p className="text-slate-600">Sensibilisez vos collaborateurs à la rédaction de mémoires techniques intégrant les aspects environnementaux.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h4 className="font-bold text-slate-900">Documentez vos engagements</h4>
                  <p className="text-slate-600">Constituez un dossier de preuves : factures de fournisseurs locaux, fiches produits, attestations.</p>
                </div>
              </div>
            </div>

            <div className="bg-violet-50 border border-violet-200 rounded-xl p-6 my-8">
              <h4 className="font-bold text-violet-900 mb-3">Le saviez-vous ?</h4>
              <p className="text-violet-800">
                Les acheteurs publics qui ne respecteront pas cette obligation s'exposent à des recours contentieux. 
                Un candidat évincé pourrait contester l'attribution d'un marché ne comportant pas de critère environnemental.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Conclusion : une opportunité pour les entreprises engagées
            </h2>

            <p>
              Loin d'être une contrainte, l'obligation du critère environnemental représente une <strong>opportunité majeure</strong> 
              pour les entreprises ayant investi dans leur transition écologique. Les PME engagées dans des démarches RSE 
              pourront désormais valoriser leurs efforts face aux grands groupes.
            </p>

            <p>
              Le 22 août 2026 marquera un tournant culturel : la commande publique passera définitivement de 
              <strong>"l'achat au prix"</strong> vers <strong>"l'achat par la valeur"</strong>.
            </p>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-4">
              Trouvez les marchés publics adaptés à vos engagements environnementaux
            </h3>
            <p className="text-violet-100 mb-6">
              Notre IA analyse les critères de chaque marché et vous recommande ceux qui correspondent à vos certifications.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-violet-700 hover:bg-slate-100">
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-4 text-center">
          <p>© 2026 Mon DCE Express - Tous droits réservés</p>
        </div>
      </footer>
    </div>
    </>
  );
}
